import { Box, Heading, Stack, Link, Text } from '@chakra-ui/react';
import { PageContainer } from '../page-container';
// import { Card } from '@ui/Card';
// import { componentReference } from '../home/content';
import { Support } from '../home/support';
import { DocsTemplateContent } from '@types';

type Props = {
  content: DocsTemplateContent;
};

export function DocsComponentReference({ content }: Props): JSX.Element {
  return (
    <PageContainer>
      <Box
        py={{ base: 12, lg: 16, xl: 24 }}
        borderBottom="1px solid"
        borderColor="var(--borderColor)"
      >
        <Box maxW="container.lg" mx="auto" px={{ base: 6, lg: 10 }}>
          <Heading
            as="h1"
            color="var(--primaryTextColor)"
            size="h4"
            fontWeight="medium"
          >
            {content.title}
          </Heading>
          <Text my={2}>
            Clerk provides a suite of components that that let you quickly add
            authenticaton to your app, customize the look and feel of your auth
            components and pages, and control what the user sees and customize
            the auth flow.
          </Text>
          <ul className="ml-4 list-outside list-disc">
            <li className="mb-2">
              Clerk's prebuilt <strong>UI components</strong> give you a
              beautiful, fully-functional user management experience in minutes.
            </li>
            <li className="mb-2">
              Clerk's <strong>control components</strong> render at{' '}
              <code>{'<Loading />'}</code> and <code>{'<Loaded />'}</code>{' '}
              states for assertions on the{' '}
              <Link href="/docs/references/javascript/clerk/clerk">
                Clerk object
              </Link>
              . Control components allow you to build custom user flows with
              Clerk.
            </li>
          </ul>
        </Box>
      </Box>

      <Stack py={12} px={{ base: 6, lg: 10 }} maxW="container.lg" mx="auto">
        <Heading
          as="h2"
          color="var(--primaryTextColor)"
          size="h5"
          fontWeight="medium"
          id="h_ui-components"
          mt={2}
        >
          Authentication Components
        </Heading>
        <div className="grid auto-rows-fr grid-cols-1 gap-x-6 md:grid-cols-2">
          <div>
            <Heading
              as="h3"
              color="var(--primaryTextColor)"
              size="h6"
              fontWeight="bold"
              mb={2}
              mt={2}
            >
              UI Components
            </Heading>
            <ul>
              <li className="truncate">
                <Link href="/docs/components/authentication/sign-in">
                  <code>{'<SignIn />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/authentication/sign-up">
                  <code>{'<SignUp />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/unstyled/sign-in-button">
                  <code>{'<SignInButton />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/unstyled/sign-up-button">
                  <code>{'<SignUpButton />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/unstyled/sign-out-button">
                  <code>{'<SignOutButton />'}</code>
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <Heading
              as="h3"
              color="var(--primaryTextColor)"
              size="h6"
              fontWeight="bold"
              mb={2}
              mt={2}
            >
              Control Components
            </Heading>
            <ul>
              <li className="truncate">
                <Link href="/docs/components/control/redirect-to-signin">
                  <code>{'<RedirectToSignIn />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/control/redirect-to-signup">
                  <code>{'<RedirectToSignUp />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/control/signed-in">
                  <code>{'<SignedIn />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/control/signed-out">
                  <code>{'<SignedOut />'}</code>
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <Heading
          as="h2"
          color="var(--primaryTextColor)"
          size="h5"
          fontWeight="medium"
          mt={6}
        >
          User Components
        </Heading>
        <div className="grid auto-rows-fr grid-cols-1 gap-x-6 md:grid-cols-2">
          <div>
            <Heading
              as="h3"
              color="var(--primaryTextColor)"
              size="h6"
              fontWeight="bold"
              mt={2}
              mb={2}
            >
              UI components
            </Heading>
            <ul>
              <li className="truncate">
                <Link href="/docs/components/user/user-button">
                  <code>{'<UserButton />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/user/user-profile">
                  <code>{'<UserProfile />'}</code>
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <Heading
              as="h3"
              color="var(--primaryTextColor)"
              size="h6"
              fontWeight="bold"
              mt={2}
              mb={2}
            >
              Control Components
            </Heading>
            <ul>
              <li className="truncate">
                <Link href="/docs/components/control/redirect-to-userprofile">
                  <code>{'<RedirectToUserProfile />'}</code>
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <Heading
          as="h2"
          color="var(--primaryTextColor)"
          size="h5"
          fontWeight="medium"
          mt={6}
        >
          Organization Components
        </Heading>
        <div className="grid auto-rows-fr grid-cols-1 gap-x-6 md:grid-cols-2">
          <div>
            <Heading
              as="h3"
              color="var(--primaryTextColor)"
              size="h6"
              fontWeight="bold"
              mt={2}
              mb={2}
            >
              UI components
            </Heading>
            <ul>
              <li className="truncate">
                <Link href="/docs/components/organization/create-organization">
                  <code>{'<CreateOrganization />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/organization/organization-profile">
                  <code>{'<OrganizationProfile />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/organization/organization-switcher">
                  <code>{'<OrganizationSwitcher />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/organization/organization-list">
                  <code>{'<OrganizationList />'}</code>
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <Heading
              as="h3"
              color="var(--primaryTextColor)"
              size="h6"
              fontWeight="bold"
              mt={2}
              mb={2}
            >
              Control components
            </Heading>
            <ul>
              <li className="truncate">
                <Link href="/docs/components/control/redirect-to-organizationprofile">
                  <code>{'<RedirectToOrganizationProfile />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/control/redirect-to-createorganization">
                  <code>{'<RedirectToCreateOrganization />'}</code>
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="grid auto-rows-fr grid-cols-1 gap-x-6 md:grid-cols-2">
          <div>
            <Heading
              as="h2"
              color="var(--primaryTextColor)"
              size="h5"
              fontWeight="medium"
              mb={2}
              mt={6}
            >
              Utility components
            </Heading>
            <ul>
              <li className="truncate">
                <Link href="/docs/components/control/clerk-loaded">
                  <code>{'<ClerkLoaded />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/control/clerk-loading">
                  <code>{'<ClerkLoading />'}</code>
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <Heading
              as="h2"
              color="var(--primaryTextColor)"
              size="h5"
              fontWeight="medium"
              mb={2}
              mt={6}
            >
              Advanced components
            </Heading>
            <ul>
              <li className="truncate">
                <Link href="/docs/components/control/authenticate-with-callback">
                  <code>{'<AuthenticateWithRedirectCallback />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/control/multi-session">
                  <code>{'<MultisessionAppSupport />'}</code>
                </Link>
              </li>
              <li className="truncate">
                <Link href="/docs/components/unstyled/sign-in-with-metamask">
                  <code>{'<SignInWithMetamaskButton />'}</code>
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <Heading
          as="h2"
          color="var(--primaryTextColor)"
          size="h5"
          fontWeight="medium"
          mb={2}
          mt={6}
        >
          Customization Guides
        </Heading>
        <ul className="md:columns-2">
          <li>
            <Link href="/docs/components/customization/overview">
              Theme components with the <code>appearance</code> prop
            </Link>
          </li>
          <li>
            <Link href="/docs/components/control/clerk-loaded">
              Localize components with the <code>localization</code> prop
              (experimental)
            </Link>
          </li>
          <li>
            <Link href="/docs/components/customization/user-profile">
              Add pages to the{' '}
              <code className="whitespace-nowrap">{'<UserProfile />'}</code>{' '}
              component
            </Link>
          </li>
          <li>
            <Link href="/docs/components/customization/organization-profile">
              Add pages to the{' '}
              <code className="whitespace-nowrap">
                {'<OrganizationProfile />'}
              </code>{' '}
              component
            </Link>
          </li>
        </ul>
        <Box pt={12}>
          <Support />
        </Box>
      </Stack>
    </PageContainer>
  );
}
